/**
 * Common Validation Rules
 *
 * Shared validation schemas and utilities used across all validators.
 * These include email format, UUID validation, tenant code format,
 * password strength, and timestamp validation.
 *
 * CRITICAL: This file is a 1:1 copy of tenant-api/src/validators/common.ts
 */

import { z } from 'zod';

// ============================================================================
// EMAIL VALIDATION
// ============================================================================

/**
 * Email validation schema
 * Validates email format according to RFC 5322 standard
 */
export const emailSchema = z
  .string({
    required_error: 'Email is required',
    invalid_type_error: 'Email must be a string',
  })
  .email('Invalid email format')
  .toLowerCase()
  .max(255, 'Email must not exceed 255 characters');

export type EmailValidation = z.infer<typeof emailSchema>;

// ============================================================================
// UUID VALIDATION
// ============================================================================

/**
 * UUID v4 validation schema
 * Used for all entity IDs in the system
 */
export const uuidSchema = z
  .string({
    required_error: 'UUID is required',
    invalid_type_error: 'UUID must be a string',
  })
  .uuid('Invalid UUID format');

export type UUIDValidation = z.infer<typeof uuidSchema>;

// ============================================================================
// TENANT CODE VALIDATION
// ============================================================================

/**
 * Tenant code validation schema
 * Format: lowercase alphanumeric, hyphens, and underscores only
 * Length: 3-50 characters
 * Examples: "acme-corp", "my_company", "startup123"
 */
export const tenantCodeSchema = z
  .string({
    required_error: 'Tenant code is required',
    invalid_type_error: 'Tenant code must be a string',
  })
  .min(3, 'Tenant code must be at least 3 characters long')
  .max(50, 'Tenant code must not exceed 50 characters')
  .toLowerCase()
  .regex(
    /^[a-z0-9_-]+$/,
    'Tenant code must contain only lowercase letters, numbers, hyphens, and underscores'
  );

export type TenantCodeValidation = z.infer<typeof tenantCodeSchema>;

// ============================================================================
// ENTITY CODE VALIDATION
// ============================================================================

/**
 * Entity code validation schema
 * Used for groups, roles, storages, service accounts, schedules, and other entities
 * Format: lowercase alphanumeric and hyphens only
 * Length: 3-50 characters
 * Examples: "engineering", "sales-team", "admin-role"
 */
export const entityCodeSchema = z
  .string({
    required_error: 'Code is required',
    invalid_type_error: 'Code must be a string',
  })
  .min(3, 'Code must be at least 3 characters long')
  .max(50, 'Code must not exceed 50 characters')
  .regex(
    /^[a-z0-9-]+$/,
    'Code must contain only lowercase letters, numbers, and hyphens'
  );

export type EntityCodeValidation = z.infer<typeof entityCodeSchema>;

// ============================================================================
// PASSWORD VALIDATION
// ============================================================================

/**
 * Password strength validation schema
 * Requirements:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 * - Maximum 128 characters
 */
export const passwordSchema = z
  .string({
    required_error: 'Password is required',
    invalid_type_error: 'Password must be a string',
  })
  .min(8, 'Password must be at least 8 characters long')
  .max(72, 'Password must not exceed 72 characters')
  .refine(
    (password) => /[A-Z]/.test(password),
    'Password must contain at least one uppercase letter'
  )
  .refine(
    (password) => /[a-z]/.test(password),
    'Password must contain at least one lowercase letter'
  )
  .refine(
    (password) => /[0-9]/.test(password),
    'Password must contain at least one number'
  )
  .refine(
    (password) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    'Password must contain at least one special character'
  );

export type PasswordValidation = z.infer<typeof passwordSchema>;

// ============================================================================
// TIMESTAMP VALIDATION
// ============================================================================

/**
 * ISO 8601 timestamp validation schema
 * Format: YYYY-MM-DDTHH:mm:ss.sssZ
 */
export const isoTimestampSchema = z
  .string({
    required_error: 'Timestamp is required',
    invalid_type_error: 'Timestamp must be a string',
  })
  .datetime('Invalid ISO 8601 timestamp format');

export type ISOTimestampValidation = z.infer<typeof isoTimestampSchema>;

/**
 * Unix timestamp validation schema (seconds since epoch)
 * Used for DynamoDB TTL
 */
export const unixTimestampSchema = z
  .number({
    required_error: 'Unix timestamp is required',
    invalid_type_error: 'Unix timestamp must be a number',
  })
  .int('Unix timestamp must be an integer')
  .positive('Unix timestamp must be positive');

export type UnixTimestampValidation = z.infer<typeof unixTimestampSchema>;

// ============================================================================
// STRING VALIDATION HELPERS
// ============================================================================

/**
 * Non-empty string schema
 * Validates that string is not empty or only whitespace
 * Note: Basic version without refine() to allow chaining
 */
export const nonEmptyStringSchema = z
  .string({
    required_error: 'Value is required',
    invalid_type_error: 'Value must be a string',
  })
  .min(1, 'Value cannot be empty')
  .trim();

/**
 * Name validation schema (first name, last name)
 * Length: 1-100 characters
 * Whitespace is automatically trimmed
 */
export const nameSchema = z
  .string({
    required_error: 'Name is required',
    invalid_type_error: 'Name must be a string',
  })
  .min(1, 'Name must be at least 1 character long')
  .max(100, 'Name must not exceed 100 characters')
  .trim();

export type NameValidation = z.infer<typeof nameSchema>;

/**
 * Description validation schema (optional)
 * Length: 0-1000 characters
 */
export const descriptionSchema = z
  .string({
    invalid_type_error: 'Description must be a string',
  })
  .max(1000, 'Description must not exceed 1000 characters')
  .optional();

export type DescriptionValidation = z.infer<typeof descriptionSchema>;

// ============================================================================
// URL VALIDATION
// ============================================================================

/**
 * URL validation schema
 * Validates HTTP/HTTPS URLs
 */
export const urlSchema = z
  .string({
    required_error: 'URL is required',
    invalid_type_error: 'URL must be a string',
  })
  .url('Invalid URL format')
  .max(2048, 'URL must not exceed 2048 characters');

export type URLValidation = z.infer<typeof urlSchema>;

// ============================================================================
// PHONE NUMBER VALIDATION
// ============================================================================

/**
 * Phone number validation schema
 * Flexible format to support international numbers
 * Allows: digits, spaces, hyphens, parentheses, plus sign
 * Empty strings are transformed to undefined
 */
export const phoneNumberSchema = z
  .string({
    invalid_type_error: 'Phone number must be a string',
  })
  .transform((val) => (val?.trim() === '' ? undefined : val?.trim()))
  .pipe(
    z
      .string()
      .min(7, 'Phone number must be at least 7 characters long')
      .max(20, 'Phone number must not exceed 20 characters')
      .regex(
        /^[\d\s\-()+]+$/,
        'Phone number can only contain digits, spaces, hyphens, parentheses, and plus sign'
      )
      .optional()
  );

export type PhoneNumberValidation = z.infer<typeof phoneNumberSchema>;

// ============================================================================
// ARRAY VALIDATION HELPERS
// ============================================================================

/**
 * Non-empty array validation
 * Ensures array has at least one element
 */
export const nonEmptyArraySchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z
    .array(itemSchema, {
      required_error: 'Array is required',
      invalid_type_error: 'Value must be an array',
    })
    .min(1, 'Array must contain at least one item');

/**
 * UUID array validation schema
 * Array of valid UUIDs
 */
export const uuidArraySchema = z.array(uuidSchema, {
  invalid_type_error: 'Value must be an array of UUIDs',
});

export type UUIDArrayValidation = z.infer<typeof uuidArraySchema>;

// ============================================================================
// PAGINATION VALIDATION
// ============================================================================

/**
 * Pagination limit validation
 * Range: 1-100 items per page
 */
export const paginationLimitSchema = z
  .number({
    invalid_type_error: 'Limit must be a number',
  })
  .int('Limit must be an integer')
  .min(1, 'Limit must be at least 1')
  .max(100, 'Limit must not exceed 100')
  .default(20);

export type PaginationLimitValidation = z.infer<typeof paginationLimitSchema>;

// ============================================================================
// VERIFICATION TOKEN VALIDATION
// ============================================================================

/**
 * Verification token validation schema
 * Used for email verification and invitation tokens
 * Length: 32-256 characters
 */
export const verificationTokenSchema = z
  .string({
    required_error: 'Verification token is required',
    invalid_type_error: 'Verification token must be a string',
  })
  .min(32, 'Verification token must be at least 32 characters long')
  .max(256, 'Verification token must not exceed 256 characters');

export type VerificationTokenValidation = z.infer<typeof verificationTokenSchema>;

// ============================================================================
// COUNTRY AND LOCATION VALIDATION
// ============================================================================

/**
 * Country name validation schema
 * Length: 2-100 characters
 * Empty strings are transformed to undefined
 */
export const countrySchema = z
  .string({
    invalid_type_error: 'Country must be a string',
  })
  .transform((val) => (val?.trim() === '' ? undefined : val?.trim()))
  .pipe(
    z
      .string()
      .min(2, 'Country must be at least 2 characters long')
      .max(100, 'Country must not exceed 100 characters')
      .optional()
  );

export type CountryValidation = z.infer<typeof countrySchema>;

/**
 * City name validation schema
 * Length: 1-100 characters
 * Empty strings are transformed to undefined
 */
export const citySchema = z
  .string({
    invalid_type_error: 'City must be a string',
  })
  .transform((val) => (val?.trim() === '' ? undefined : val?.trim()))
  .pipe(
    z
      .string()
      .min(1, 'City must be at least 1 character long')
      .max(100, 'City must not exceed 100 characters')
      .optional()
  );

export type CityValidation = z.infer<typeof citySchema>;

/**
 * Address validation schema
 * Length: 1-500 characters
 * Empty strings are transformed to undefined
 */
export const addressSchema = z
  .string({
    invalid_type_error: 'Address must be a string',
  })
  .transform((val) => (val?.trim() === '' ? undefined : val?.trim()))
  .pipe(
    z
      .string()
      .min(1, 'Address must be at least 1 character long')
      .max(500, 'Address must not exceed 500 characters')
      .optional()
  );

export type AddressValidation = z.infer<typeof addressSchema>;

// ============================================================================
// REQUIRED FIELD SCHEMAS (for registration)
// ============================================================================

/**
 * Required address schema (for registration)
 * Length: 1-500 characters
 * Unlike addressSchema, this does NOT allow empty strings
 */
export const addressRequiredSchema = z
  .string({
    required_error: 'Address is required',
    invalid_type_error: 'Address must be a string',
  })
  .min(1, 'Address is required')
  .max(500, 'Address must not exceed 500 characters')
  .trim();

export type AddressRequiredValidation = z.infer<typeof addressRequiredSchema>;

/**
 * Required phone number schema (for registration)
 * Unlike phoneNumberSchema, this does NOT allow empty strings
 */
export const phoneNumberRequiredSchema = z
  .string({
    required_error: 'Phone number is required',
    invalid_type_error: 'Phone number must be a string',
  })
  .min(7, 'Phone number must be at least 7 characters')
  .max(20, 'Phone number must not exceed 20 characters')
  .regex(
    /^[\d\s\-()+]+$/,
    'Phone number can only contain digits, spaces, hyphens, parentheses, and plus sign'
  );

export type PhoneNumberRequiredValidation = z.infer<typeof phoneNumberRequiredSchema>;
